import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Phone, Mail, MapPin } from 'lucide-react';
import SEOHead from '@/components/SEOHead';

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen pt-20 bg-white" data-testid="privacy-policy-page">
      <SEOHead 
        title="Privacy Policy | Amy C. Brown Law"
        description="Privacy policy for Amy C. Brown Law website. Learn how we collect, use, and protect your personal information."
      />
      {/* Header */}
      <section className="py-12 bg-secondary">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <div className="flex items-center gap-3 text-primary mb-4">
            <Shield className="w-6 h-6" />
            <span className="text-sm font-medium uppercase tracking-wider">Legal</span>
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
            Privacy Policy
          </h1>
          <p className="text-slate-300">
            Last Updated: December 2025
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <div className="prose prose-lg max-w-none">
            
            <h2 className="font-heading text-2xl font-bold text-secondary mt-8 mb-4">Introduction</h2>
            <p className="text-slate-700 mb-6">
              Amy C. Brown Law ("we," "us," or "our") respects your privacy and is committed to protecting your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website amycbrownlaw.com or contact our office.
            </p>

            <h2 className="font-heading text-2xl font-bold text-secondary mt-8 mb-4">Information We Collect</h2>
            <p className="text-slate-700 mb-4">We may collect personal information that you voluntarily provide to us, including:</p>
            <ul className="list-disc pl-6 text-slate-700 mb-6 space-y-2">
              <li>Name, email address, and phone number when you contact us</li>
              <li>Details about your legal matter that you share during consultations</li>
              <li>Information collected automatically through cookies and similar technologies</li>
            </ul>

            <h2 className="font-heading text-2xl font-bold text-secondary mt-8 mb-4">How We Use Your Information</h2>
            <p className="text-slate-700 mb-4">We use the information we collect to:</p>
            <ul className="list-disc pl-6 text-slate-700 mb-6 space-y-2">
              <li>Respond to your inquiries and provide legal services</li>
              <li>Evaluate potential legal matters</li>
              <li>Communicate with you about your case or our services</li>
              <li>Improve our website and services</li>
              <li>Comply with legal obligations</li>
            </ul>

            <h2 className="font-heading text-2xl font-bold text-secondary mt-8 mb-4">Attorney-Client Privilege</h2>
            <p className="text-slate-700 mb-6">
              Please note that submitting information through our website does not create an attorney-client relationship. However, once an attorney-client relationship is established, all communications are protected by attorney-client privilege and kept strictly confidential in accordance with the Washington Rules of Professional Conduct.
            </p>

            <h2 className="font-heading text-2xl font-bold text-secondary mt-8 mb-4">Information Sharing</h2>
            <p className="text-slate-700 mb-4">We do not sell, trade, or rent your personal information. We may share your information only:</p>
            <ul className="list-disc pl-6 text-slate-700 mb-6 space-y-2">
              <li>With your consent</li>
              <li>To comply with legal obligations or court orders</li>
              <li>With service providers who assist in operating our website (under confidentiality agreements)</li>
              <li>To protect our rights, privacy, safety, or property</li>
            </ul>

            <h2 className="font-heading text-2xl font-bold text-secondary mt-8 mb-4">Cookies and Tracking</h2>
            <p className="text-slate-700 mb-6">
              Our website may use cookies and similar tracking technologies to enhance your browsing experience and analyze website traffic. You can control cookie settings through your browser preferences.
            </p>

            <h2 className="font-heading text-2xl font-bold text-secondary mt-8 mb-4">Data Security</h2>
            <p className="text-slate-700 mb-6">
              We implement reasonable security measures to protect your personal information. However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.
            </p>

            <h2 className="font-heading text-2xl font-bold text-secondary mt-8 mb-4">Your Rights</h2>
            <p className="text-slate-700 mb-6">
              You have the right to access, correct, or delete your personal information. To exercise these rights or ask questions about our privacy practices, please contact us using the information below.
            </p>

            <h2 className="font-heading text-2xl font-bold text-secondary mt-8 mb-4">Changes to This Policy</h2>
            <p className="text-slate-700 mb-6">
              We may update this Privacy Policy from time to time. The updated version will be indicated by the "Last Updated" date at the top of this page.
            </p>

            <h2 className="font-heading text-2xl font-bold text-secondary mt-8 mb-4">Contact Us</h2>
            <p className="text-slate-700 mb-4">If you have questions about this Privacy Policy, please contact us:</p>
            <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
              <p className="font-bold text-secondary mb-3">Amy C. Brown Law</p>
              <div className="space-y-2 text-slate-700">
                <p className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-primary" />
                  <a href="tel:425-675-1224" className="hover:text-primary">(425) 675-1224</a>
                </p>
                <p className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-primary" />
                  <a href="mailto:amy@amycbrownlaw.com" className="hover:text-primary">amy@amycbrownlaw.com</a>
                </p>
                <p className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-primary" />
                  Everett, Washington
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicyPage;
